import {
  invalid,
  readArray,
  readCount,
  readEnum,
  readMoney,
  readNonEmptyString,
  readNullableString,
  readRecord,
  readString,
} from '../readers';
import {
  REPORT_PERIODS,
  type ReportDay,
  type ReportKeySpend,
  type ReportRow,
  type ReportSkuSpend,
  type UsageReport,
} from './model';

const readDate = (value: unknown, label: string): string => {
  const date = readString(value, label);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return invalid(label);
  return date;
};

const readSku = (value: unknown): ReportSkuSpend => {
  const row = readRecord(value, 'report API row');
  return {
    sku: readNonEmptyString(row.sku, 'report API endpoint'),
    api: readString(row.api, 'report API name'),
    requests: readCount(row.requests, 'report API request count'),
    costUsd: readMoney(row.costUsd, 'report API USD'),
  };
};

const readKey = (value: unknown): ReportKeySpend => {
  const row = readRecord(value, 'report key row');
  return {
    keyId: readNullableString(row.keyId, 'report key id'),
    name: readString(row.name, 'report key name'),
    requests: readCount(row.requests, 'report key request count'),
    costUsd: readMoney(row.costUsd, 'report key USD'),
  };
};

const readDay = (value: unknown): ReportDay => {
  const row = readRecord(value, 'report day');
  return {
    date: readDate(row.date, 'report day date'),
    requests: readCount(row.requests, 'report day request count'),
    costUsd: readMoney(row.costUsd, 'report day USD'),
  };
};

const readRow = (value: unknown): ReportRow => {
  const row = readRecord(value, 'report row');
  return {
    date: readDate(row.date, 'report row date'),
    sku: readNonEmptyString(row.sku, 'report row endpoint'),
    api: readString(row.api, 'report row API name'),
    requests: readCount(row.requests, 'report row request count'),
    costUsd: readMoney(row.costUsd, 'report row USD'),
  };
};

/** Reads `GET /v1/usage/report`, refusing any figure it cannot vouch for. */
export const readUsageReport = (value: unknown): UsageReport => {
  const report = readRecord(value, 'usage report');
  return {
    period: readEnum(report.period, REPORT_PERIODS, 'report period'),
    timeZone: readNonEmptyString(report.timeZone, 'report time zone'),
    from: readDate(report.from, 'report start date'),
    to: readDate(report.to, 'report end date'),
    asOf: readNonEmptyString(report.asOf, 'report time'),
    totalRequests: readCount(report.totalRequests, 'report request count'),
    totalCostUsd: readMoney(report.totalCostUsd, 'report USD'),
    avgCostPer1kUsd: readMoney(report.avgCostPer1kUsd, 'report USD per 1,000 requests'),
    bySku: readArray(report.bySku, 'report API rows').map(readSku),
    byKey: readArray(report.byKey, 'report key rows').map(readKey),
    daily: readArray(report.daily, 'report days').map(readDay),
    rows: readArray(report.rows, 'report rows').map(readRow),
  };
};
